import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Wrench } from 'lucide-react';
import { api } from '../api/client';
import { Badge, ErrorState, Skeleton, Spinner, useFetch, useToast } from '../components/ui';

interface Iface {
  name: string;
  running: boolean;
  type: string;
  addresses: string[];
  network: string | null;
  dhcpGateway: string | null;
}
interface Discovery {
  identity: string | null;
  version: string | null;
  interfaces: Iface[];
  hasMainDefaultRoute: boolean;
  foreignPccRules: number;
  installedRules: number;
  rollbackPending: boolean;
  wanList: string[];
}
interface Plan {
  commands: string[];
  warnings: string[];
  lines: Array<{ interface: string; gateway: string; mark: string; table: string }>;
}

const MODES = [
  { value: 'both-addresses', label: 'Both addresses — spreads by source and destination (recommended)' },
  { value: 'src-address', label: 'Source address — each client sticks to one line' },
  { value: 'both-addresses-and-ports', label: 'Addresses and ports — finest spread, can upset some sites' },
];

/** Install PCC load balancing on a MikroTik: pick the lines, review the exact commands, apply, then confirm. */
export function InstallBalancer() {
  const { id } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const { data, error, loading, reload } = useFetch(() => api.get<Discovery>(`/api/plugins/routers/lb/${id}/install/discover`), [id]);
  const d = data as Discovery | null;

  const [picked, setPicked] = useState<Record<string, string>>({}); // interface -> gateway override
  const [mode, setMode] = useState('both-addresses');
  const [plan, setPlan] = useState<Plan | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const body = () => ({
    mode,
    wans: Object.entries(picked).map(([name, gateway]) => ({ interface: name, gateway: gateway.trim() || undefined })),
  });

  const run = async (what: string, fn: () => Promise<void>) => {
    setBusy(what);
    setMsg(null);
    try {
      await fn();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(null);
    }
  };

  if (loading && !d) return <Skeleton height={300} />;
  if (error || !d) return <ErrorState error={error ?? 'Could not read the router'} onRetry={reload} />;

  const candidates = d.interfaces;

  return (
    <div className="col" style={{ gap: 14, maxWidth: 900 }}>
      <div className="page-header">
        <div className="grow">
          <div className="page-subtitle">
            <Link to={`/resources/${id}`}>Back to resource</Link>
          </div>
          <h1 className="page-title">Install load balancing</h1>
          <div className="page-subtitle">
            {d.identity ?? 'MikroTik'} {d.version && `· RouterOS ${d.version}`}
          </div>
        </div>
      </div>

      {d.rollbackPending && (
        <div className="card" style={{ padding: '10px 14px', borderLeft: '3px solid var(--itr-warning)' }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            An install is waiting for your confirmation. If you do nothing, the router removes it by itself a few minutes after it was applied.
          </div>
          <button
            className="btn"
            disabled={busy !== null}
            onClick={() =>
              void run('keep', async () => {
                const r = await api.post<{ balancer?: boolean }>(`/api/plugins/routers/lb/${id}/install/keep`, {});
                toast('Load balancing kept', 'good');
                nav(r.balancer ? `/resources/${id}/balancer` : `/resources/${id}`);
              })
            }
          >
            Keep it
          </button>
        </div>
      )}

      {d.foreignPccRules > 0 && (
        <div className="card" style={{ padding: '10px 14px', borderLeft: '3px solid var(--itr-critical)', fontSize: 13 }}>
          This router already has {d.foreignPccRules} PCC rule{d.foreignPccRules === 1 ? '' : 's'} that were not created here. Installing over them would fight them, so it is blocked.
        </div>
      )}

      {d.installedRules > 0 ? (
        <div className="card" style={{ padding: '10px 14px', fontSize: 13 }}>
          <div style={{ marginBottom: 8 }}>Load balancing installed from here is on this router ({d.installedRules} rules).</div>
          <button
            className="btn"
            disabled={busy !== null}
            onClick={() => {
              if (!confirm('Remove everything this portal installed? Traffic returns to the main routing table.')) return;
              void run('remove', async () => {
                await api.post(`/api/plugins/routers/lb/${id}/install/remove`, { confirmed: true });
                toast('Load balancing removed', 'good');
                reload();
              });
            }}
          >
            Remove load balancing
          </button>
        </div>
      ) : (
        d.foreignPccRules === 0 && (
          <>
            <div className="card">
              <div className="card-header">
                <span className="card-title">1. Choose the internet lines</span>
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th />
                    <th>Port</th>
                    <th>Link</th>
                    <th>Address</th>
                    <th>Gateway (modem / ISP router)</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((i) => {
                    const on = i.name in picked;
                    return (
                      <tr key={i.name}>
                        <td>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={(e) => {
                              const next = { ...picked };
                              if (e.target.checked) next[i.name] = '';
                              else delete next[i.name];
                              setPicked(next);
                              setPlan(null);
                            }}
                          />
                        </td>
                        <td className="mono">{i.name}</td>
                        <td>
                          <Badge intent={i.running ? 'good' : 'neutral'}>{i.running ? 'up' : 'no link'}</Badge>
                        </td>
                        <td className="mono">{i.addresses[0] ?? <span className="subtle">none</span>}</td>
                        <td>
                          {on && (
                            <input
                              className="input"
                              style={{ width: 150 }}
                              placeholder={i.dhcpGateway ?? 'e.g. 192.168.1.1'}
                              value={picked[i.name]}
                              onChange={(e) => {
                                setPicked({ ...picked, [i.name]: e.target.value });
                                setPlan(null);
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="card-body">
                <label className="row" style={{ gap: 8, fontSize: 13 }}>
                  Balancing
                  <select className="input" value={mode} onChange={(e) => { setMode(e.target.value); setPlan(null); }}>
                    {MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn"
                disabled={busy !== null || Object.keys(picked).length < 2}
                onClick={() =>
                  void run('plan', async () => setPlan(await api.post<Plan>(`/api/plugins/routers/lb/${id}/install/plan`, body())))
                }
              >
                {busy === 'plan' ? 'Checking…' : '2. Review the changes'}
              </button>
              {Object.keys(picked).length < 2 && <span className="subtle" style={{ fontSize: 12.5 }}>Pick at least two lines.</span>}
            </div>
          </>
        )
      )}

      {msg && <div style={{ fontSize: 13, color: 'var(--itr-critical)' }}>{msg}</div>}
      {busy === 'install' && <Spinner label="Applying on the router…" />}

      {plan && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">What will be done</span>
          </div>
          <div className="card-body col" style={{ gap: 8 }}>
            {plan.warnings.map((w) => (
              <div key={w} style={{ fontSize: 13 }}>• {w}</div>
            ))}
            <details>
              <summary style={{ fontSize: 13, cursor: 'pointer' }}>The {plan.commands.length} RouterOS commands</summary>
              <pre className="mono" style={{ fontSize: 11.5, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{plan.commands.join('\n')}</pre>
            </details>
            <div>
              <button
                className="btn"
                disabled={busy !== null}
                onClick={() => {
                  if (!confirm('Apply this to the router now?\n\nIf it cannot be reached afterwards, it undoes itself after a few minutes.')) return;
                  void run('install', async () => {
                    const r = await api.post<{ message: string }>(`/api/plugins/routers/lb/${id}/install`, { ...body(), confirmed: true });
                    toast(r.message, 'good');
                    setPlan(null);
                    reload();
                  });
                }}
              >
                <Wrench size={13} /> Install
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
